import {
  makeObservable,
  observable,
  action,
  flow,
  computed,
  autorun,
  when,
  toJS,
} from "mobx";
import _ from "lodash";
import { v4 } from "uuid";
import * as richModalChatService from "../../services/richmodal";
import {
  CHAT_CHANNEL,
  CLIENT_CHAN,
  CHAT_ON_R1,
  CHAT_ON_VIDEO_EDIT,
  CHAT_ON_VIDEO_COM,
  EChatErrorCode,
  STREAM_END_MSG,
} from "./constants";
import { ResponseData } from "../../utils/request";
import { ResCode } from "logic/src/constants/request";
import { copy } from "../../utils";
import { ERichModalFileParseStatus } from "../file/types";
import {
  ESessionFileType,
  ESessionStatus,
  ISessionFile,
} from "../session/type";
import SessionFile from "../session/sessionFile";
import {
  EChatType,
  IChatMessage,
  EStreamDataStatus,
  EStreamDataType,
  EChatRecvStatus,
  IChatRecvMessage,
  IChatSendMessage,
  EChatScore,
  EChatStatus,
  ERegenerateJudge,
  ESendMsgType,
  IChatTextMessage,
  EChatQueryType,
  EChatModel,
} from "./types";
import BaseStreamRender from "./BaseStreamRenderV2";
import { sendCLK } from "../../utils/eventTrack";

class Chat {
  id: string = "";
  startTime: number = Date.now();
  @observable chatModel: EChatModel = EChatModel.Normal;
  @observable type: EChatType = EChatType.history;
  @observable streamingStatus: EStreamDataStatus = EStreamDataStatus.init;
  @observable send_msg?: IChatSendMessage[];
  @observable files?: ISessionFile[]; // 用户上传文件
  @observable recv_msg?: IChatRecvMessage[] | null = null; // 历史数据
  @observable.ref current_msg?: IChatRecvMessage | null = null; // 当前流式数据
  cached_msg?: IChatRecvMessage[] | null = null; // 当前缓存流式数据
  @observable complete?: boolean;
  @observable errCode?: number;
  @observable score?: EChatScore;
  @observable session_status?: ESessionStatus;
  // @observable status: "key_point" | "work" | EChatStatus = "work";
  @observable status: EChatStatus = EChatStatus.work;
  @observable isRegen: boolean = false;
  @observable isRegenFeedback: boolean = false;
  @observable llm: string = "";

  render: BaseStreamRender;

  web_search: boolean | undefined = undefined;

  guess: boolean = false;

  send_time?: string;
  recv_time?: string;
  session_id: string = "";
  parent_id?: string;
  @observable message_id?: string;
  guidance?: string;
  private abort?: () => void = () => {};

  constructor(
    initChat: IChatMessage,
    public onSessionIdChange?: (value: string) => void,
    public onSessionStatusChange?: (value: ESessionStatus) => void,
    public onChatSteamEnd?: (v: Chat) => void,
  ) {
    makeObservable(this);
    this.type = initChat.type;
    this.guidance = initChat.guidance;
    this.send_msg = initChat.send_msg;
    this.files = initChat.files;
    this.complete = initChat.complete;
    this.recv_msg = initChat.recv_msg;
    this.setCurrentMsg(initChat.current_msg);
    this.cached_msg = initChat.cached_msg;
    this.message_id = initChat.message_id;
    if (initChat.message_id) {
      this.id = initChat.message_id;
    } else {
      this.id = v4();
    }
    this.parent_id = initChat.parent_id;
    this.session_id = initChat.session_id;
    this.send_time = initChat.send_time;
    this.recv_time = initChat.recv_time;
    this.status = initChat.status;
    this.errCode = initChat.errCode;
    this.guess = initChat.guess || false;
    this.score =
      initChat.score === undefined ? EChatScore.default : initChat.score;
    this.setStreamingStatus(initChat.streamingStatus);
    this.isRegenFeedback = false;
    this.chatModel = initChat.chatModel || EChatModel.Normal;
    this.web_search = initChat.web_search;
    this.render = new BaseStreamRender({
      type: initChat.type,
    });

    if (initChat.recv_msg && initChat.recv_msg.length > 0) {
      this.render.batchWrite(initChat.recv_msg);
      const lastMsg = initChat.recv_msg[initChat.recv_msg.length - 1];
      if (
        initChat.type === EChatType.history &&
        lastMsg.status !== EChatRecvStatus.end
      ) {
        this.render.write(STREAM_END_MSG);
      }
    }

    autorun(() => {
      if (
        this.guidance &&
        this.streamingStatus === EStreamDataStatus.streamingEnd
      ) {
        this.setRecvMsg(this.cached_msg);
      }
    });
    if (initChat.type === EChatType.stream) {
      this.preAsk();
    }
  }

  @computed
  get initialSessionTitle() {
    let titleStr = this.textMessageStr || "";
    if (this.isTransMsg) {
      const transMsg = this.send_msg?.find(
        item => item.msg_type === ESendMsgType.trans_query,
      );
      transMsg && (titleStr = `${transMsg.trans_query}${titleStr}`);
    }
    return titleStr.substring(0, 21);
  }

  @computed
  get allFileIds() {
    let list: any[] = [];
    this.files?.length && list.push(...this.files);
    return list.filter(ele => !!ele.file_id).map(i => i.file_id);
  }

  @computed
  get isLinkFileReady() {
    if (!this.files || !this.files.length) return true;
    return !this.files.some(
      ele =>
        ele.type === ESessionFileType.link &&
        ele.status === ERichModalFileParseStatus.running,
    );
  }

  @computed
  get isSensitive() {
    return (
      (this.errCode &&
        [
          EChatErrorCode.inputSensitive,
          EChatErrorCode.outputSensitive,
          EChatErrorCode.bothSensitive,
        ].includes(this.errCode)) ||
      [EChatStatus.input, EChatStatus.output, EChatStatus.bothOutput].includes(
        this.status,
      )
    );
  }

  get isThinkSensitive() {
    return (
      this.errCode === EChatErrorCode.thinkSensitive ||
      this.errCode === EChatErrorCode.bothSensitive ||
      [EChatStatus.thinkOutput, EChatStatus.bothOutput].includes(this.status)
    );
  }

  @computed
  get isBusy() {
    return (
      this.errCode === EChatErrorCode.userBusy ||
      this.status === EChatStatus.busy
    );
  }

  @computed
  get isContentEmpty() {
    return this.type === EChatType.history
      ? !this.recv_msg?.length
      : !this.cached_msg?.length;
  }

  @computed
  get allTextStr() {
    const msgList =
      this.type === EChatType.history ? this.recv_msg : this.cached_msg;
    if (!msgList || !msgList.length) {
      return "";
    }

    const reasonTextList = msgList
      .filter((item: any) => item.type === "text")
      .map((txtItem: any) => txtItem.reasoning_content);
    const textList = msgList
      .filter((item: any) => item.type === "text")
      .map((txtItem: any) => txtItem.text);
    let thinkingStr = reasonTextList.join("");
    if (thinkingStr) {
      thinkingStr = `<think> ${thinkingStr} </think>`;
    }
    return thinkingStr + textList.join("");
  }

  @computed
  get inputMessageList() {
    const textMsg = this.send_msg?.find(
      ele => ele.msg_type === ESendMsgType.user_query,
    );

    const splitedMsgList = SessionFile.splitUrlAndStr(
      textMsg?.user_query || "",
    );

    const usedFileIds: string[] = [];
    const res: IChatTextMessage[] = [];

    splitedMsgList.forEach(ele => {
      if (ele.type === "url") {
        const linkFile = _.find(
          this.files,
          (o: ISessionFile) =>
            o.file_name === ele.content &&
            !usedFileIds.includes(o.file_id || ""),
        );
        usedFileIds.push(linkFile?.file_id || "");
        res.push({ ...ele, fileId: linkFile?.file_id || "" });
      } else {
        res.push(ele);
      }
    });
    return res;
  }

  @computed
  get textMessageStr() {
    const textMsg = this.send_msg?.find(
      ele => ele.msg_type === ESendMsgType.user_query,
    );
    return textMsg ? textMsg.user_query : "";
  }

  @computed
  get canRegenFeedback() {
    return (
      this.streamingStatus === EStreamDataStatus.streamingEnd &&
      this.isRegen &&
      !this.isRegenFeedback
    );
  }

  @computed
  get isRoot() {
    return !this.parent_id || this.parent_id === "0";
  }

  @computed
  get isTransMsg() {
    return this.send_msg?.some(
      item => item.msg_type === ESendMsgType.trans_query,
    );
  }

  @computed
  get chatQueryType() {
    // 判断顺序不可调整
    if (this.isRegen) return EChatQueryType.regeneration;
    if (this.isTransMsg) return EChatQueryType.translation;
    if (this.files?.length) {
      return this.files.some(item => item.type === ESessionFileType.image)
        ? EChatQueryType.picture
        : EChatQueryType.file;
    }
    if (this.guess) return EChatQueryType.guess;
    return EChatQueryType.wordage;
  }

  setIsRegen(v: boolean) {
    this.isRegen = v;
  }

  @action
  setStreamingStatus(value: EStreamDataStatus) {
    this.streamingStatus = value;
    if (value === EStreamDataStatus.streamingEnd) {
      this.onChatSteamEnd?.(this);
    }
  }

  @action
  setType(v: EChatType) {
    this.type = v;
  }

  @action
  setStatus(v: EChatStatus) {
    this.status = v;
  }

  @action
  setCurrentMsg(v: IChatRecvMessage | null) {
    this.current_msg = v;
  }

  @action
  cacheCurrentMsg(v: IChatRecvMessage) {
    if (Array.isArray(this.cached_msg)) {
      this.cached_msg.push(v);
    } else {
      this.cached_msg = [v];
    }
  }

  @action
  setRecvMsg(v: IChatRecvMessage[] | null | undefined) {
    this.recv_msg = v;
  }

  @action
  setStreamMessageData(value: IChatRecvMessage) {
    this.setCurrentMsg(value);
    this.cacheCurrentMsg(value);
    this.render?.write(value);
  }

  @action
  setErrCode(value: number) {
    this.errCode = value;
  }

  @action
  handleSessionIdChange(value: string) {
    this.session_id = value;
    this.onSessionIdChange?.(value);
  }

  @action
  handleSessionStatusChange(value: ESessionStatus) {
    this.session_status = value;
    this.onSessionStatusChange?.(value);
  }

  @action
  private resetChat() {
    this.recv_msg = null;
    this.message_id = undefined;
    this.score = EChatScore.default;
    this.errCode = undefined;
    this.complete = undefined;
    this.setCurrentMsg(null);
    this.cached_msg = null;
    this.setIsRegen(false);
    this.isRegenFeedback = false;
    this.setType(EChatType.stream);
    this.setStreamingStatus(EStreamDataStatus.init);
    this.render.clear();
  }

  async preAsk() {
    await when(() => this.isLinkFileReady);
    this.ask("next");
  }

  public reAsk() {
    this.ask("regeneration");
  }

  public ask(action: "next" | "regeneration") {
    try {
      this.resetChat();
      if (action === "regeneration") {
        this.setStreamingStatus(EStreamDataStatus.init);
        this.setIsRegen(true);
      }
      const { exec, abort } = richModalChatService.chat(
        {
          guidance: this.guidance,
          action,
          session_id: this.session_id,
          send_msg: this.send_msg || [],
          channel: CHAT_CHANNEL,
          client_chan: this.chatModel || CLIENT_CHAN,
          parent_id: this.parent_id,
          file_ids: this.allFileIds || [],
          query_type: this.chatQueryType,
          web_search: this.web_search,
        },
        {
          onMessage: this.handleOnMessage,
          onFinish: () => {
            if (this.streamingStatus !== EStreamDataStatus.streamingEnd) {
              this.setStreamingStatus(EStreamDataStatus.streamingEnd);
            }
          },
          onError: error => {
            this.setErrCode(EChatErrorCode.default);
            this.addStreamEndMsg();
          },
        },
      );
      exec();
      this.abort = abort;
    } catch (error) {}
  }

  @action.bound
  public async stop() {
    console.log("------stop", this.session_id);
    await this.abort?.();
    this.complete = false;
    this.addStreamEndMsg();
  }

  private handleOnMessage = (res: {
    code: number;
    message: {
      session_id: string;
      message_id: string;
      delta: IChatRecvMessage;
    };
  }) => {
    const { message, code } = res;
    const { session_id, message_id, delta } = message || {
      session_id: "",
      message_id: "",
      delta: {},
    };

    !!session_id &&
      this.session_id !== session_id &&
      this.handleSessionIdChange(session_id);

    !!message_id &&
      this.message_id !== message_id &&
      (this.message_id = message_id);

    if (code === ResCode.Success) {
      if (message_id || !!this.guidance) {
        const currentStatus =
          delta!.type === EStreamDataType.status &&
          delta!.status === EChatRecvStatus.end
            ? EStreamDataStatus.streamingEnd
            : EStreamDataStatus.streaming;

        this.setStreamMessageData(delta!);
        this.streamingStatus !== currentStatus &&
          this.setStreamingStatus(currentStatus);
        if (
          delta.type === EStreamDataType.status &&
          delta.status === EChatRecvStatus.text2image
        ) {
          sendCLK("ai_drawing_initiate", "ai绘画_触发生图");
        }
        if (delta.type === EStreamDataType.text2image) {
          sendCLK("ai_drawing_success", "ai绘画_生图成功");
        }
      }
    } else {
      if (code === 2) {
        this.setStatus(EChatStatus.key_point);
      }

      if (
        code === EChatErrorCode.outputSensitive &&
        this.errCode === EChatErrorCode.thinkSensitive
      ) {
        this.setErrCode(EChatErrorCode.bothSensitive);
      } else {
        this.setErrCode(code);
      }
      // 思维链敏感不停止
      if (this.errCode !== EChatErrorCode.thinkSensitive) {
        this.addStreamEndMsg();
      }
    }
  };

  // 处理非正常结束
  private addStreamEndMsg() {
    this.setStreamMessageData(STREAM_END_MSG);
    this.setStreamingStatus(EStreamDataStatus.streamingEnd);
  }

  // 点赞/点踩/取消反馈
  @flow
  *feedback({
    score,
    tag,
    onSuccess,
    onFailure,
  }: {
    score: EChatScore;
    tag?: string[];
    onSuccess?: () => void;
    onFailure?: () => void;
  }): Generator<Promise<ResponseData>, void, any> {
    try {
      const { code } = yield richModalChatService.chatFeedback({
        score,
        tag: !!tag ? tag : [],
        session_id: this.session_id,
        message_id: this.message_id!,
      });
      if (code === ResCode.Success) {
        onSuccess?.();
        this.score = score;
      } else {
        onFailure?.();
      }
    } catch (error) {
      onFailure?.();
    }
  }

  // 重新生成评价
  @flow.bound
  *judgeRegenerateMsg(v: ERegenerateJudge) {
    try {
      yield richModalChatService.judgeRegenerateMsg({
        session_id: this.session_id,
        message_id: this.message_id!,
        parent_id: this.parent_id,
        compare_rating: v,
        channel: CHAT_CHANNEL,
      });
      this.isRegenFeedback = true;
    } catch (error) {}
  }

  // 复制
  @action
  handleCopy(
    event: React.MouseEvent,
    onSuccess: () => void,
    removeThink?: boolean,
  ) {
    if (!event) return;
    event.preventDefault();
    copy(
      removeRefTags(
        removeThink ? removeThinkTags(this.allTextStr) : this.allTextStr,
      ),
      onSuccess,
    );
  }
}

function removeThinkTags(text: string) {
  // 使用正则表达式匹配<think>开头和</think>结尾的所有内容（包括嵌套的情况）
  const regex = /^<think>[\s\S]*?<\/think>/;

  return text.replace(regex, "");
}

function removeRefTags(str: string) {
  return str.replace(/\[ref_\d+\]/g, "");
}

export default Chat;
